/** P-03 — patient profile (name, language, timezone, push master kill). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { describeError } from "../../api/errors";
import { getLocales, getMe, patchProfile } from "../../api/patient";
import { useAuth } from "../../auth/AuthContext";
import { ChangePasswordCard } from "../../components/ChangePasswordCard";
import { Select } from "../../components/ui/Select";

// The browser's IANA time zones, offered as suggestions; the API refuses an unknown one (ACCT-08).
const TIME_ZONES: string[] = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];

export function Profile() {
  const qc = useQueryClient();
  const { renameUser } = useAuth();
  const { data } = useQuery({ queryKey: ["me"], queryFn: getMe });
  // Locale list is config-driven and effectively static — no need to refetch per mount.
  const { data: localeData } = useQuery({ queryKey: ["locales"], queryFn: getLocales, staleTime: Infinity });
  const [form, setForm] = useState({ full_name: "", timezone: "", language_preference: "en", push_master_kill: false });
  const [saved, setSaved] = useState(false);
  const locales = localeData?.locales ?? [];

  useEffect(() => {
    if (data)
      setForm({
        full_name: data.full_name,
        timezone: data.timezone,
        language_preference: data.language_preference,
        push_master_kill: data.push_master_kill,
      });
  }, [data]);

  const save = useMutation({
    // The name is trimmed, as the API stores it (AUTH-22: 2-120 characters).
    mutationFn: () => patchProfile({ ...form, full_name: form.full_name.trim(), timezone: form.timezone.trim() }),
    onSuccess: (me) => {
      qc.invalidateQueries({ queryKey: ["me"] });
      renameUser(me.full_name);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  return (
    <div className="max-w-lg space-y-8">
      <div className="space-y-4">
      <h2 className="text-lg font-semibold">Profile</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
        className="space-y-4 rounded-xl border bg-white p-5 shadow-xs"
      >
        <Field label="Full name">
          <input
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            required
            minLength={2}
            maxLength={120}
            autoComplete="name"
            className="w-full rounded-lg border px-3 py-1.5"
          />
        </Field>
        {/* One column on a phone, so a long time zone isn't clipped (RESP-20). */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Language">
            <Select
              value={form.language_preference}
              options={[
                ...locales.map((l) => ({ value: l.code, label: l.label })),
                // A saved locale no longer in SUPPORTED_LOCALES would otherwise show blank and be
                // silently rewritten on save: keep it visible until the user changes it.
                ...(form.language_preference && !locales.some((l) => l.code === form.language_preference)
                  ? [{ value: form.language_preference, label: `${form.language_preference} (unsupported)` }]
                  : []),
              ]}
              onChange={(language_preference) => setForm({ ...form, language_preference })}
              className="w-full"
            />
          </Field>
          <Field label="Timezone">
            <input
              value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
              list="time-zones"
              required
              placeholder="Asia/Kolkata"
              className="w-full rounded-lg border px-3 py-1.5"
            />
            <datalist id="time-zones">
              {TIME_ZONES.map((tz) => <option key={tz} value={tz} />)}
            </datalist>
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.push_master_kill} onChange={(e) => setForm({ ...form, push_master_kill: e.target.checked })} />
          Pause all push notifications
        </label>
        <div className="flex items-center gap-3 pt-1 text-sm text-gray-500">
          <span>Phone: {data?.phone ?? "—"}</span>
        </div>
        <div className="flex items-center gap-3">
          <button className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg" disabled={save.isPending}>
            Save
          </button>
          {saved && <span className="text-sm text-emerald-600">Saved</span>}
          {save.isError && <span className="text-sm text-red-600">{describeError(save.error)}</span>}
        </div>
      </form>
      </div>

      {data && (
        <ChangePasswordCard
          passwordSet={data.password_set}
          onPasswordSet={() => qc.invalidateQueries({ queryKey: ["me"] })}
        />
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-gray-500">{label}</span>
      {children}
    </label>
  );
}
